import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";

import DataSvc from "../services/dataSvc";

const REFRESH_BUFFER_MS = 60 * 1000;

export const useAuthTokenRefresh = () => {
  const { isAuthenticated, getAccessTokenSilently, getIdTokenClaims } =
    useAuth0();
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    DataSvc.setSessionExpiredHandler(() => setSessionExpired(true));
    return () => DataSvc.setSessionExpiredHandler(undefined);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const refresh = async () => {
      try {
        await getAccessTokenSilently();
        const claims = await getIdTokenClaims();
        if (cancelled) {
          return;
        }
        DataSvc.setToken(Promise.resolve(claims?.__raw || ""));
        scheduleFromClaims();
      } catch (error) {
        if (!cancelled) {
          setSessionExpired(true);
        }
      }
    };

    const scheduleFromClaims = async () => {
      const claims = await getIdTokenClaims();
      if (cancelled || !claims?.exp) {
        return;
      }
      const msUntilRefresh = Math.max(
        claims.exp * 1000 - Date.now() - REFRESH_BUFFER_MS,
        0
      );
      timeoutId = setTimeout(refresh, msUntilRefresh);
    };

    scheduleFromClaims();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, getAccessTokenSilently, getIdTokenClaims]);

  return { sessionExpired };
};
